
import { supabase } from "@/integrations/supabase/client";
import { LearningPath, LearningPathModule, LearningModule } from "@/types/learning";

export async function getLearningPaths() {
  const { data, error } = await supabase
    .from('learning_paths')
    .select('*')
    .order('title');

  if (error) {
    console.error('Error fetching learning paths:', error);
    throw error;
  }

  return data.map(path => ({
    id: path.id,
    title: path.title,
    description: path.description || '',
    difficulty: path.difficulty || 'Beginner',
    estimatedHours: path.estimated_hours || 0,
    imageUrl: path.image_url,
    modules: [],
    progress: 0
  })) as LearningPath[];
}

export async function getLearningPathById(pathId: string, userId?: string) {
  // Fetch the learning path
  const { data: pathData, error: pathError } = await supabase
    .from('learning_paths')
    .select('*')
    .eq('id', pathId)
    .single();

  if (pathError) {
    console.error('Error fetching learning path:', pathError);
    throw pathError;
  }

  // Fetch the modules associated with this path
  const { data: moduleData, error: moduleError } = await supabase
    .from('learning_path_modules')
    .select(`
      id,
      path_id,
      module_id,
      order_index,
      is_required,
      learning_modules:module_id (id, title, description, difficulty, content)
    `)
    .eq('path_id', pathId)
    .order('order_index');

  if (moduleError) {
    console.error('Error fetching learning path modules:', moduleError);
    throw moduleError;
  }

  // If we have a userId, fetch the user's progress for these modules
  let userProgress: Record<string, boolean> = {};
  if (userId) {
    const { data: progressData, error: progressError } = await supabase
      .from('user_progress')
      .select('module_id, completed')
      .eq('user_id', userId)
      .in('module_id', moduleData.map(m => m.module_id));

    if (!progressError && progressData) {
      userProgress = progressData.reduce((acc, curr) => {
        acc[curr.module_id] = curr.completed || false;
        return acc;
      }, {} as Record<string, boolean>);
    }
  }

  // Calculate overall progress
  const completedModules = moduleData.filter(m => 
    userProgress[m.module_id] && m.is_required
  ).length;
  const totalRequiredModules = moduleData.filter(m => m.is_required).length;
  const progressPercentage = totalRequiredModules 
    ? Math.round((completedModules / totalRequiredModules) * 100) 
    : 0;

  // Format modules with user progress
  const modules = moduleData.map(m => ({
    id: m.id,
    moduleId: m.module_id,
    pathId: m.path_id,
    orderIndex: m.order_index,
    isRequired: m.is_required,
    isCompleted: userProgress[m.module_id] || false,
    module: {
      id: m.learning_modules?.id,
      title: m.learning_modules?.title || 'Unknown Module',
      description: m.learning_modules?.description || '',
      level: m.learning_modules?.difficulty || 'Beginner',
      duration: '30 min' // Default duration if not specified
    }
  })) as LearningPathModule[];

  // Construct the full learning path with modules
  return {
    id: pathData.id,
    title: pathData.title,
    description: pathData.description || '',
    difficulty: pathData.difficulty || 'Beginner',
    estimatedHours: pathData.estimated_hours || 0,
    imageUrl: pathData.image_url,
    modules,
    progress: progressPercentage
  } as LearningPath;
}

export async function getLearningModuleById(moduleId: string, userId?: string) {
  // Fetch the module
  const { data: moduleData, error: moduleError } = await supabase
    .from('learning_modules')
    .select('*')
    .eq('id', moduleId)
    .single();

  if (moduleError) {
    console.error('Error fetching learning module:', moduleError);
    throw moduleError;
  }

  // Find which path this module belongs to (if any)
  const { data: pathModuleData } = await supabase
    .from('learning_path_modules')
    .select('path_id')
    .eq('module_id', moduleId)
    .maybeSingle();

  // Check if user has completed this module
  let isCompleted = false;
  if (userId) {
    const { data: progressData } = await supabase
      .from('user_progress')
      .select('completed')
      .eq('user_id', userId)
      .eq('module_id', moduleId)
      .maybeSingle();

    isCompleted = progressData?.completed || false;
  }

  return {
    id: moduleData.id,
    title: moduleData.title,
    description: moduleData.description || '',
    content: moduleData.content || '',
    level: moduleData.difficulty || 'Beginner',
    duration: '30 min', // Default duration if not specified
    isCompleted,
    pathId: pathModuleData?.path_id
  } as LearningModule & { isCompleted?: boolean, pathId?: string };
}

export async function updateModuleProgress(userId: string, moduleId: string, completed: boolean) {
  // Check if a progress record already exists
  const { data: existingProgress } = await supabase
    .from('user_progress')
    .select('id')
    .eq('user_id', userId)
    .eq('module_id', moduleId)
    .maybeSingle();

  if (existingProgress) {
    // Update existing record
    const { error } = await supabase
      .from('user_progress')
      .update({ 
        completed, 
        completion_date: completed ? new Date().toISOString() : null 
      })
      .eq('id', existingProgress.id);

    if (error) {
      console.error('Error updating module progress:', error);
      throw error;
    }
  } else {
    // Create new progress record
    const { error } = await supabase
      .from('user_progress')
      .insert({ 
        user_id: userId, 
        module_id: moduleId, 
        completed,
        completion_date: completed ? new Date().toISOString() : null
      });

    if (error) {
      console.error('Error creating module progress:', error);
      throw error;
    }
  }

  return { success: true };
}
